import { generateToDocFn } from '@dadohr/shared/domainTypes/document';
import { GLOBAL_CONFIG_ID } from '@dadohr/shared/domainTypes/globalConfig';
import { FS } from '@dadohr/shared/versions';

import { store, useMappedLoadingValue } from '../firebase';
import {
  createDocumentListenerGetter,
  useDocumentListener
} from '../firecache/documentListener';

import { Announcement } from './logic';

export * from './logic';

/**
 * The feature-announcement lives on the shared global-config doc (globalConfigV1/config)
 * under `announcement`. It is not part of the published IGlobalConfig type (services are
 * BE-only / the FE reads config fields defensively), so we type just the slice we need.
 */
interface GlobalConfigAnnouncementSlice {
  announcement?: Announcement;
}

const configDocRef = () => store().collection(FS.globalConfig).doc(GLOBAL_CONFIG_ID);

const toDoc = generateToDocFn<GlobalConfigAnnouncementSlice>();

const cache = createDocumentListenerGetter<GlobalConfigAnnouncementSlice>(
  () => configDocRef(),
  toDoc,
  () => ({})
);

/** Live listener for the current feature announcement (undefined when none set). */
export const useAnnouncement = () =>
  useMappedLoadingValue(useDocumentListener(cache('')), (d) => d?.data?.announcement);

/**
 * Super-admin: save the announcement URL/title/active and bump the version so it shows
 * once to everyone. Preserves all other global-config fields (full-doc set with spread,
 * mirroring enableService in admin/features/GlobalConfig).
 */
export const saveAnnouncement = (fields: {
  videoUrl: string;
  title?: string;
  active: boolean;
}) =>
  store().runTransaction(async (t) => {
    const ref = configDocRef();
    const snap = await t.get(ref);
    const d = (snap.data() as (GlobalConfigAnnouncementSlice & Record<string, unknown>)) || {};
    const prevVersion = d.announcement?.version ?? 0;
    return t.set(ref, {
      ...d,
      announcement: {
        videoUrl: fields.videoUrl.trim(),
        title: fields.title?.trim() ?? '',
        active: fields.active,
        version: prevVersion + 1
      }
    });
  });

/**
 * Super-admin: re-show the current announcement to everyone (bump version only, keep the
 * same URL/title/active).
 */
export const reshowAnnouncement = () =>
  store().runTransaction(async (t) => {
    const ref = configDocRef();
    const snap = await t.get(ref);
    const d = (snap.data() as (GlobalConfigAnnouncementSlice & Record<string, unknown>)) || {};
    const prev = d.announcement;
    if (!prev) return undefined;
    return t.set(ref, {
      ...d,
      announcement: { ...prev, version: (prev.version ?? 0) + 1 }
    });
  });
